import { Link as RouterLink } from 'react-router-dom'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Chip from '@mui/material/Chip'
import MuiLink from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import type { Document, Manifest } from '../../api/authoring'
import { pageFields } from '../../model/page'
import { routes } from '../../model/routes'
import { nameOrId } from '../../model/name'
import { UndoButton } from '../../ui/UndoButton'
import { PublicationStatus } from '../site/PublicationStatus'

/** Which face of the page is open. The tab strip and the address agree. */
export type PageFace = 'content' | 'preview' | 'structure' | 'settings'

/**
 * Everything above the page's four faces, written once.
 *
 * **It was written twice, and the two had drifted.** Three faces are
 * `ContentPage` and the fourth is `PageEditor`, and each had built its
 * own breadcrumbs, its own title row and its own copy of the same tab
 * strip. The results disagreed about the two things a header is for:
 * Structure put the publication chip in a row of chips under the title
 * and the undo button on a line of its own below that, so moving
 * between faces moved both controls and changed the height of the page
 * under the reader's cursor. They also disagreed about how to name the
 * site — one asked `websiteFields().title`, the other `nameOrId` — and
 * about what an untitled page is called.
 *
 * A header is the one part of a screen that must not move when the
 * screen changes, so it stops being each face's own markup.
 *
 * **What it deliberately does not carry is the model.** Structure drew
 * the page's type and `id · revision` beside its name; the other three
 * drew neither, and they were right. A reader on a page knows it is a
 * page, the id is in the address bar, and the revision is a fact about
 * the document rather than about the page — the one screen where an
 * operator genuinely asks "which component is this" is the component
 * library, and `ComponentEditor` still says so there. Structure is the
 * face that shows the model *inside the page*: link ids, pins,
 * placements. That is a different claim from printing a uuid over the
 * title on every visit.
 */
export function PageHeader({
  tenant,
  site,
  pageId,
  page,
  website,
  manifest,
  face,
  params,
  urlPath,
  sending,
}: {
  tenant: string
  site: string
  pageId: string
  page: Document
  /** The site this page is in, for the first crumb. */
  website: Document | undefined
  manifest: Manifest | undefined
  face: PageFace
  /** The query the faces carry between them, so a selection survives a tab. */
  params: URLSearchParams
  /** Where this page answers, for the publication chip. Absent where nothing routes to it. */
  urlPath: string | undefined
  sending: boolean
}) {
  const title = pageFields(page).title || 'Untitled page'
  const to = (pathname: string) => ({ pathname, search: params.toString() })

  return (
    <Stack spacing={1}>
      {/* The site, then the list this page is in, then the page. Two
          crumbs above rather than one, because the site's own address is
          now its Overview and the thing a reader climbing out of a page
          wants is the pages.

          The site by its name, never by its id: one face printed a uuid
          where the other three printed "Bluu Interactive", on the one
          line whose whole job is to say where the reader is. */}
      <Breadcrumbs>
        <MuiLink component={RouterLink} to={routes.site(tenant, site)}>
          {website ? nameOrId(website, manifest) : site}
        </MuiLink>
        <MuiLink component={RouterLink} to={routes.pages(tenant, site)}>
          Pages
        </MuiLink>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      {/* **One row, and the same row on every face.** The name takes the
          space, and the two things that are about the page as a whole —
          whether it is published, and undoing the last thing done to it
          — sit at the end of it.

          Undo stays in the header rather than moving to the face that
          holds a page's own controls. It is used seconds after a mistake
          made on any of the four, and a face away is where it cannot be
          used. */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        {/* The one piece of the document's own state that is not
            navigation: a deleted page still opens, and a reader who
            cannot see that it is deleted has no way to know why the site
            does not show it. */}
        {page.tombstoned && <Chip size="small" color="error" label="deleted" />}
        <PublicationStatus tenant={tenant} site={site} path={urlPath} page />
        {/* On the page rather than on whatever block is selected,
            because that is what a revert addresses: one component's
            stream. An inline block has none of its own, so undoing an
            edit to one is undoing the page's last change — which is
            exactly what this sends. A linked block is its own component
            and is undone from its own screen. */}
        <UndoButton tenant={tenant} document={page} manifest={manifest} disabled={sending} />
      </Stack>

      {/* Links, not buttons — a face is an address, so each tab is
          somewhere to go and a colleague can be sent any of them. The
          query rides along, so a reader who checks the frame or the
          page's address comes back to the block they had open.

          Preview sits next to Content rather than last, because those
          two are the pair an owner moves between all afternoon — change
          the words, check it looks right. Structure is last because it
          is the one they open least: it was another mode reached by a
          toggle at the top of the window, which is why an owner who
          needed one thing from it — a link id, a pin — had to leave the
          page to get there. Settings is what they touch when a page is
          made and then rarely again. */}
      <Tabs value={face} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Content" value="content" component={RouterLink} to={to(routes.page(tenant, site, pageId))} />
        <Tab label="Preview" value="preview" component={RouterLink} to={to(routes.pagePreview(tenant, site, pageId))} />
        <Tab
          label="Structure"
          value="structure"
          component={RouterLink}
          to={to(routes.pageStructure(tenant, site, pageId))}
        />
        <Tab
          label="Settings"
          value="settings"
          component={RouterLink}
          to={to(routes.pageSettings(tenant, site, pageId))}
        />
      </Tabs>
    </Stack>
  )
}
